// ─── Recibir dinero Brasil · Side panel (PIX) ───────────────────────────────────────────────────────────
// The BRL wallet's receive panel: the COP panel's drawer for the one PIX rail (`RailDepositDrawer`). The payer
// is named, with a CPF or a CNPJ, because the provider needs both to mint the dynamic QR; the result is the QR
// beside its copia e cola code, with the time it still holds.
//
// Uses the NestJS payments runtime (`POST /internal/payments/payins`) — fees are computed server-side.

import { Zap } from "lucide-react";

import {
  buildBrazilPixPayinPayload,
  PayinRail,
} from "@/modules/dashboard/payins/services/paymentsPayin.service";
import { brazilDocumentType, brazilPayerType, formatBrazilDocument } from "@/shared/utils/brazilDocument";

import type { Wallet } from "../types/wallets-api.types";
import { BrazilPayerStep } from "./deposit/BrazilPayerStep";
import type { BrazilPayerValues } from "./deposit/depositPayer.schema";
import { PixPaymentInstrument } from "./deposit/PixPaymentInstrument";
import { type RailDepositCorridor, RailDepositDrawer } from "./deposit/RailDepositDrawer";

export interface BrazilDepositModalProps {
  wallet: Wallet;
  open: boolean;
  onClose: () => void;
}

const BRAZIL_PIX: RailDepositCorridor<BrazilPayerValues> = {
  rail: PayinRail.PIX,
  country: "BRA",
  currency: "BRL",
  amountHintKey: "deposit_amount_hint_bra",
  methodHintKey: "deposit_method_pix_hint",
  processingKey: "deposit_processing_pix",
  methodIcon: <Zap className="size-5" />,
  initialPayer: (user) => ({ payerName: user?.full_name ?? "", document: "" }),
  PayerStep: BrazilPayerStep,
  payerRows: (payer, t) => [
    { label: t("deposit_confirm_name"), value: payer.payerName.trim() },
    {
      label: brazilDocumentType(payer.document) ?? t("deposit_confirm_document"),
      value: formatBrazilDocument(payer.document),
    },
  ],
  payerType: (payer) => brazilPayerType(payer.document),
  buildPayload: ({ organizationId, grossAmount, payer, idempotencyKey }) =>
    buildBrazilPixPayinPayload({
      organizationId,
      amountBrl: grossAmount,
      payerName: payer.payerName,
      payerDocument: payer.document,
      idempotencyKey,
    }),
  Instrument: PixPaymentInstrument,
};

export function BrazilDepositModal({ wallet, open, onClose }: BrazilDepositModalProps) {
  return <RailDepositDrawer wallet={wallet} open={open} onClose={onClose} corridor={BRAZIL_PIX} />;
}
